// ============================================================================
// MOCK ROUTES — maps the Solene dataset into each endpoint's exact wire shape.
// Envelope shapes and field names verified against the Adaya backend.
// All task-based endpoints return data inline (no taskId) to skip polling.
// ============================================================================
import { demoAccount } from './demoConfig';
import { getDataset, sumRange, seriesRange, Metrics } from './dataset';
import { round0, round2 } from './mockUtils';

const NOW_ISO = () => new Date().toISOString();

// Normalize any date input (ISO datetime OR "YYYY-MM-DD") to a date-only key.
const dayKey = (v: any): string => String(v).slice(0, 10);

function range(query: any): { from: string; to: string } {
    const ds = getDataset();
    const first = ds.dates[0];
    const last = ds.dates[ds.dates.length - 1];
    const clamp = (d: string) => (d < first ? first : d > last ? last : d);
    let from = query && (query.from || query.start_date) ? clamp(dayKey(query.from || query.start_date)) : first;
    let to = query && (query.to || query.end_date) ? clamp(dayKey(query.to || query.end_date)) : last;
    // Never allow an empty/inverted window (e.g. a stale future date range): fall
    // back to the last 30 days of the dataset so a screen is never blank.
    if (from > to) {
        to = last;
        from = ds.dates[Math.max(0, ds.dates.length - 30)];
    }
    return { from, to };
}

// The campaign-data "totals" object shape (backend Totals model).
// Raw sums are what matter; the app recomputes cpp/aov/roas/mer/profit/scores.
function totalsOf(m: Metrics) {
    const items = round0(m.orders * 1.3);
    const checkouts = round0(m.orders * 1.35);
    return {
        sales: round2(m.revenue),
        adspend: round2(m.adspend),
        spend: round2(m.adspend),
        orders: m.orders,
        revenue: round2(m.revenue),
        items,
        pageviews: m.pageview,
        sessions: m.sessions,
        addToCarts: m.addToCarts,
        checkouts,
        cpp: m.cpp,
        aov: m.aov,
        mer: 0,
        roas: m.roas,
        profit: round2(m.profit),
        scores: {
            conversionRate: m.conversionRate,
            addToCartConversionRate: m.addToCarts > 0 ? round2((m.orders / m.addToCarts) * 100) : 0,
            itemsPerCustomer: 1.3,
            conversionIndex: 100,
            addToCartConversionIndex: 100,
            itemsPerCustomerIndex: 100,
            score: m.scores,
        },
    };
}

// Build a DailyAccountData[] for [from,to]. Full campaign>adset>ad hierarchy is
// delivered under the "Adaya" provider (revenue is only kept for Adaya/Shopify),
// with each campaign tagged provider:'Facebook' so it reads as a Meta campaign.
function campaignData(from: string, to: string) {
    const ds = getDataset();
    const days = ds.dates.filter((d) => d >= from && d <= to);
    return days.map((day) => {
        const campaigns = ds.campaigns.map((c) => ({
            id: c.id,
            name: c.name,
            provider: 'Facebook',
            totals: totalsOf(c.daily[day]),
            adsets: c.adsets.map((a) => ({
                id: a.id,
                name: a.name,
                totals: totalsOf(a.daily[day]),
                ads: a.ads.map((ad) => ({
                    id: ad.id,
                    name: ad.name,
                    totals: totalsOf(ad.daily[day]),
                })),
            })),
        }));
        return {
            accountId: demoAccount._id,
            day,
            providers: [
                {
                    name: 'Adaya',
                    isComplete: true,
                    updated: NOW_ISO(),
                    data: { campaigns, totals: totalsOf(ds.accountDaily[day]) },
                },
            ],
        };
    });
}

type Handler = (ctx: { params: string[]; query: any; data: any; config: any; path: string }) => any;
interface Route { method: string; pattern: RegExp; handler: Handler; }

export const ROUTES: Route[] = [
    // ---- CORE FEED: campaign-data ----
    {
        method: 'get',
        pattern: /^\/account-data\/[^/]+\/campaign-data$/,
        handler: ({ query }) => {
            const { from, to } = range(query);
            return { success: true, data: campaignData(from, to) };
        },
    },

    // ---- tracking-data (grouped by campaign / landing_page) ----
    {
        method: 'get',
        pattern: /^\/account-data\/[^/]+\/tracking-data$/,
        handler: ({ query }) => {
            const { from, to } = range(query);
            const ds = getDataset();
            const acct = sumRange(ds.accountDaily, from, to);
            const toTracking = (m: Metrics) => ({
                orders: m.orders,
                items: round0(m.orders * 1.3),
                revenue: round2(m.revenue),
                profit: round2(m.profit),
                spend: round2(m.adspend),
                purchase: m.orders,
                'add-to-cart': m.addToCarts,
                sessions: m.sessions,
            });
            const rows = ds.campaigns.map((c) => {
                const m = sumRange(c.daily, from, to);
                return { id: c.id, provider: 'Facebook', name: c.name, totals: toTracking(m) };
            });
            return { success: true, data: { totals: toTracking(acct), data: rows } };
        },
    },

    // ---- shopify revenue (GET + POST job) ----
    {
        method: 'get',
        pattern: /^\/account-data\/[^/]+\/shopify\/revenue$/,
        handler: ({ query }) => ({ success: true, revenues: shopifyRevenues(query) }),
    },
    {
        method: 'post',
        pattern: /^\/account-data\/[^/]+\/shopify\/revenue(\/job)?$/,
        handler: ({ data }) => ({ success: true, revenues: shopifyRevenues(data || {}) }),
    },

    // ---- MER prediction ----
    {
        method: 'get',
        pattern: /^\/account-data\/[^/]+\/predictions\/mer$/,
        handler: ({ query }) => {
            const ds = getDataset();
            const last = ds.dates[ds.dates.length - 1];
            const m = sumRange(ds.accountDaily, ds.dates[ds.dates.length - 30], last);
            const mer = m.adspend > 0 ? round2(m.revenue / m.adspend) : 3.3;
            return {
                success: true,
                day: (query && query.day) || last,
                dailyMER: { prediction: mer, lower: round2(mer * 0.86), upper: round2(mer * 1.14) },
            };
        },
    },

    // ---- facebook/spend ----
    {
        method: 'get',
        pattern: /^\/account-data\/[^/]+\/facebook\/spend$/,
        handler: ({ query }) => {
            const { from, to } = range(query);
            const ds = getDataset();
            const days = ds.dates.filter((d) => d >= from && d <= to);
            const result = days.map((day) => {
                const m = ds.accountDaily[day];
                return {
                    day,
                    providers: [
                        {
                            name: 'Facebook',
                            isComplete: true,
                            updated: NOW_ISO(),
                            data: { spend: round2(m.adspend), revenue: round2(m.revenue), purchases: m.orders, ctr: 1.8, actionAttributes: [] },
                        },
                    ],
                };
            });
            return { success: true, result };
        },
    },

    // ---- revenue-goal predictions (Goal pages) ----
    {
        method: 'get',
        pattern: /^\/users\/revenue-goal\/([^/]+)$/,
        handler: ({ params }) => {
            const month = params[0]; // "YYYY-MM"
            const ds = getDataset();
            const goals = (demoAccount as any); // monthly goal from brand config
            const monthlyGoal = 850000;
            const daysInMonth = new Date(parseInt(month.slice(0, 4)), parseInt(month.slice(5, 7)), 0).getDate();
            const dailyGoal = Math.round(monthlyGoal / daysInMonth);
            const predictions = ds.dates
                .filter((d) => d.startsWith(month))
                .map((d) => ({ day: d, goal: dailyGoal, predicted: round0(ds.accountDaily[d].revenue) }));
            return { success: true, month, predictions };
        },
    },

    // ---- providers list ----
    {
        method: 'get',
        pattern: /^\/providers$/,
        handler: () => ({
            success: true,
            providers: [
                { name: 'Adaya', alwayShow: false },
                { name: 'Facebook', alwayShow: true },
                { name: 'Shopify', alwayShow: true },
                { name: 'Google', alwayShow: true },
                { name: 'Klaviyo', alwayShow: true },
                { name: 'TikTok', alwayShow: true },
                { name: 'Amazon_Ads', alwayShow: true },
            ],
        }),
    },

    // ---- exchange rates ----
    {
        method: 'get',
        pattern: /^\/exchange-rates$/,
        handler: () => ({
            success: true,
            rates: {
                date: NOW_ISO(),
                rates: {
                    eur: { code: 'EUR', rate: 0.92, inverseRate: 1.087 },
                    gbp: { code: 'GBP', rate: 0.78, inverseRate: 1.282 },
                    cad: { code: 'CAD', rate: 1.37, inverseRate: 0.73 },
                    aud: { code: 'AUD', rate: 1.49, inverseRate: 0.671 },
                },
            },
        }),
    },

    // ---- admin: active accounts (account switcher, admin user) ----
    {
        method: 'get',
        pattern: /^\/admin\/users\/activeAccounts$/,
        handler: () => ({ success: true, accounts: [{ _id: demoAccount._id, name: demoAccount.name, accountId: demoAccount.accountId, currency: demoAccount.currency }] }),
    },
    {
        method: 'get',
        pattern: /^\/admin\/users\/accounts$/,
        handler: () => ({ success: true, users: [] }),
    },

    // ---- auth check-session (not used in DEMO_MODE, here for safety) ----
    {
        method: 'post',
        pattern: /^\/auth\/check-session$/,
        handler: () => ({ success: true, user: null }),
    },

    // ---- tracking events (attribution feed / purchase journeys) ----
    // Return correctly-keyed empty arrays so pages render (enriched separately).
    {
        method: 'post',
        pattern: /^\/tracking\/purchase-journey$/,
        handler: () => ({ success: true, journey: [], events: [] }),
    },
    {
        method: 'post',
        pattern: /^\/tracking\/(events|purchase-events|purchase-events-non-attributed)/,
        handler: () => ({ success: true, events: [], data: [] }),
    },
    {
        method: 'post',
        pattern: /^\/tracking\//,
        handler: () => ({ success: true, data: [], events: [], stats: {} }),
    },

    // ---- task status (only if some path still polls) ----
    {
        method: 'get',
        pattern: /^\/tasks\/check-status\//,
        handler: () => ({ status: 'finished', progress: 100, error: null, result: [] }),
    },
];

function shopifyRevenues(q: any) {
    const { from, to } = range(q || {});
    const ds = getDataset();
    return ds.dates
        .filter((d) => d >= from && d <= to)
        .map((day) => {
            const m = ds.accountDaily[day];
            return { day, sales: round2(m.revenue), revenue: round2(m.revenue), orders: m.orders, refunds: 0, isComplete: true };
        });
}
